import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showWarning, showError } from '../utils/sweetAlert';
import logger from '../utils/logger';

const MetalProductsManufacturing = () => {
  const navigate = useNavigate();
  const [messaging, setMessaging] = useState({});
  const [benchmarks, setBenchmarks] = useState({});
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ROI Calculator state
  const [roiInputs, setRoiInputs] = useState({
    annual_electricity_spend_aud: '',
    annual_gas_spend_aud: '',
    has_furnace: false,
    has_compressed_air: false,
    site_floor_area_m2: '',
    production_hours_per_week: ''
  });
  const [roiResults, setRoiResults] = useState(null);
  const [calculatingROI, setCalculatingROI] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchMetalProductsManufacturingData();
  }, []);

  const fetchMetalProductsManufacturingData = async () => {
    try {
      setLoading(true);
      
      const [messagingRes, benchmarksRes, caseStudiesRes] = await Promise.all([
        fetch(`${backendUrl}/api/metal-products-manufacturing/messaging`),
        fetch(`${backendUrl}/api/metal-products-manufacturing/benchmarks`),
        fetch(`${backendUrl}/api/metal-products-manufacturing/case-studies`)
      ]);

      if (messagingRes.ok) {
        const messagingData = await messagingRes.json();
        setMessaging(messagingData);
      }

      if (benchmarksRes.ok) {
        const benchmarksData = await benchmarksRes.json();
        setBenchmarks(benchmarksData);
      }

      if (caseStudiesRes.ok) {
        const caseStudiesData = await caseStudiesRes.json();
        setCaseStudies(caseStudiesData.case_studies || []);
      }

    } catch (error) {
      logger.error('Error fetching Metal Products Manufacturing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateROI = async () => {
    try {
      setCalculatingROI(true);
      
      const electricitySpend = parseFloat(roiInputs.annual_electricity_spend_aud.replace(/,/g, ''));
      const gasSpend = parseFloat(roiInputs.annual_gas_spend_aud.replace(/,/g, '')) || 0;
      const floorArea = parseFloat(roiInputs.site_floor_area_m2.replace(/,/g, '')) || 0;
      const productionHours = parseFloat(roiInputs.production_hours_per_week) || 0;
      
      if (!electricitySpend || electricitySpend <= 0) {
        showWarning('Invalid Input', 'Please enter a valid electricity spend amount');
        return;
      }

      const requestData = {
        annual_electricity_spend_aud: electricitySpend,
        annual_gas_spend_aud: gasSpend,
        has_furnace: roiInputs.has_furnace,
        has_compressed_air: roiInputs.has_compressed_air,
        site_floor_area_m2: floorArea,
        production_hours_per_week: productionHours
      };

      const response = await fetch(`${backendUrl}/api/metal-products-manufacturing/calculate-roi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setRoiResults(result);
      } else {
        logger.error('ROI calculation failed');
        showError('Calculation Failed', 'ROI calculation failed. Please try again.');
      }

    } catch (error) {
      logger.error('Error calculating ROI:', error);
      showError('Calculation Error', 'Error calculating ROI. Please try again.');
    } finally {
      setCalculatingROI(false);
    }
  };

  const handleStartAssessment = () => {
    navigate('/assessment/self-guided?industry=Metal%20Products%20Manufacturing&source=metal-products-landing');
  };

  const handleViewSampleReport = () => {
    navigate('/sample-esa-report?industry=metal-products-manufacturing');
  };

  const handlePurchaseReport = () => {
    navigate('/payment-checkout?industry=metal-products-manufacturing&amount=990');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading Metal Products Manufacturing analysis...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-emerald-600 font-bold text-xl">
                Rely Ability
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Metal Products Manufacturing Specialists</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Industry Image */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                {messaging.hero_headline || 'Reduce Metal Manufacturing Energy Costs by 28%+'}
              </h1>
              <p className="text-xl mb-8 text-slate-200">
                {messaging.hero_subheadline || 'Professional energy analysis for fabricated metals, foundries, machining, and metalworking operations.'}
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {(messaging.value_propositions || [
                  'Furnace Optimization',
                  'Compressed Air Efficiency', 
                  'Motor & Drive Systems',
                  'Heat Recovery Systems'
                ]).map((prop, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-emerald-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{prop}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleStartAssessment}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                >
                  {messaging.cta_primary || 'Get YOUR Metal Manufacturing Analysis FREE'}
                </button>
                <button
                  onClick={handleViewSampleReport}
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-4 rounded-lg font-semibold transition-colors"
                >
                  {messaging.cta_secondary || 'Live Analysis Engine'}
                </button>
              </div>
            </div>

            {/* Industry Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1695603414724-2396274ef5cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtZXRhbCUyMG1hbnVmYWN0dXJpbmd8ZW58MHx8fHwxNzU4OTM1MzQxfDA&ixlib=rb-4.1.0&q=85"
                alt="Professional worker operating modern metal fabrication equipment"
                className="w-full h-96 object-cover rounded-xl shadow-2xl"
              />
              <div className="absolute bottom-6 left-6 text-white">
                <div className="text-sm opacity-75">Australian Metal Products Manufacturing</div>
                <div className="text-lg font-semibold">Professional Energy Assessment</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benchmarks Highlight */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Metal Products Manufacturing Industry Insights</h2>
            <p className="text-lg text-gray-600">Based on completed assessments from our assessment database</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="bg-emerald-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                {benchmarks.total_assessments || 1141}
              </div>
              <div className="text-sm text-gray-600">Assessments Completed</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                {benchmarks.median_savings_potential ? formatCurrency(benchmarks.median_savings_potential) : '$128K'}
              </div>
              <div className="text-sm text-gray-600">Median Savings</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">28%</div>
              <div className="text-sm text-gray-600">Average Reduction</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                {benchmarks.average_payback_years || 2.2} yrs
              </div>
              <div className="text-sm text-gray-600">Avg Payback</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="bg-gray-50 py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {(messaging.trust_signals || [
              'OSHA Compliant',
              'High Temperature Rated', 
              'Industrial Grade Solutions',
              'Australian Standards'
            ]).map((signal, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm font-medium text-gray-900">{signal}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Calculate Your Metal Manufacturing Savings
            </h2>
            <p className="text-lg text-gray-600">
              Get personalized savings estimate for your metal fabrication facility
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-6">Facility Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Annual Electricity Spend (AUD) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="text"
                        value={roiInputs.annual_electricity_spend_aud}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9,]/g, '');
                          setRoiInputs(prev => ({...prev, annual_electricity_spend_aud: value}));
                        }}
                        placeholder="485,000"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Annual Gas Spend (AUD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="text"
                        value={roiInputs.annual_gas_spend_aud}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9,]/g, '');
                          setRoiInputs(prev => ({...prev, annual_gas_spend_aud: value}));
                        }}
                        placeholder="225,000"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Equipment Types
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={roiInputs.has_furnace}
                          onChange={(e) => setRoiInputs(prev => ({...prev, has_furnace: e.target.checked}))}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm">Furnaces or High-Temperature Equipment</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={roiInputs.has_compressed_air}
                          onChange={(e) => setRoiInputs(prev => ({...prev, has_compressed_air: e.target.checked}))}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm">Compressed Air Systems</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={calculateROI}
                    disabled={calculatingROI}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    {calculatingROI ? 'Calculating...' : 'Calculate My Savings'}
                  </button>
                </div>
              </div>

              <div>
                {roiResults ? (
                  <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6 border border-emerald-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Your Savings Potential</h3>
                    
                    <div className="space-y-4">
                      <div className="text-center bg-white rounded-lg p-4 border border-emerald-100">
                        <div className="text-3xl font-bold text-emerald-600">
                          {formatCurrency(roiResults.potential_annual_savings_aud)}
                        </div>
                        <div className="text-sm text-gray-600">Annual Savings Potential</div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                          <div className="font-bold text-lg text-gray-900">{roiResults.savings_percentage}%</div>
                          <div className="text-xs text-gray-600">Energy Reduction</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                          <div className="font-bold text-lg text-gray-900">{roiResults.simple_payback_years} yrs</div>
                          <div className="text-xs text-gray-600">Payback Period</div>
                        </div>
                      </div>

                      <div className="bg-emerald-600 text-white rounded-lg p-4 text-center">
                        <div className="text-sm opacity-90 mb-1">{roiResults.benchmark_position}</div>
                        <div className="font-bold">Professional analysis available</div>
                      </div>

                      <div className="text-center">
                        <button
                          onClick={handlePurchaseReport}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                        >
                          Get Detailed Report - $990
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="text-gray-500 mb-4">
                      <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-600">Enter your facility details to calculate potential savings</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      {caseStudies.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Success Stories</h2>
              <p className="text-lg text-gray-600">Real results from Australian metal products manufacturers</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {caseStudies.map((study, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold mb-4">{study.company_name}</h3>
                  <p className="text-gray-600 mb-4">{study.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="text-emerald-600 font-bold text-lg">
                      {formatCurrency(study.annual_savings)}
                    </div>
                    <div className="text-sm text-gray-500">Annual Savings</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-emerald-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Reduce Your Metal Manufacturing Energy Costs?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Join 240+ Australian metal products manufacturers who've reduced costs
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleStartAssessment}
              className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              Start Free Assessment
            </button>
            <button
              onClick={handleViewSampleReport}
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-4 rounded-lg font-semibold transition-colors"
            >
              View Sample Analysis
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MetalProductsManufacturing;